import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaUserCheck } from 'react-icons/fa6';
import { Container, Table, Spinner, Alert } from 'react-bootstrap';
import useApiFetch from '../../hooks/useApiFetch';
import CustomButton from '../button/button';
import { availableLanguages } from '../data/languageData';
import { Modal } from "react-bootstrap";
import { updatePostEditJobAction } from '../../redux/actions/translationAction';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import errorToast from '../toasts/errorToast';
import successToast from '../toasts/successToast';

/* ----------------------------------------------------
   Helpers
---------------------------------------------------- */

const findCodeByUniqueId = (uniqueId, codes) => {
    if (!uniqueId || !codes) return null;
    const [idStr, tagType] = uniqueId.split('_');
    const id = parseInt(idStr, 10);
    return codes.find(
        c =>
            c.id === id &&
            (c.tagType || 'PLACEHOLDER') === (tagType || 'PLACEHOLDER')
    );
};

const buildTextWithCodes = (text, codes) => {
    if (!codes || codes.length === 0) return text || '';
    let result = text || '';
    [...codes]
        .sort((a, b) => b.position - a.position)
        .forEach((c) => {
            const pos = Math.max(0, Math.min(result.length, c.position));
            result = result.slice(0, pos) + (c.data || '') + result.slice(pos);
        });
    return result;
};

const toHtmlWithIds = (text, codes) => {
    if (!text && (!codes || codes.length === 0)) {
        return '<div></div>';
    }

    let resultHtml = (text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const sortedCodes = codes
        .map((code, idx) => ({ code, idx }))
        .sort((a, b) => {
            if (b.code.position !== a.code.position) {
                return b.code.position - a.code.position;
            }
            return b.idx - a.idx;
        })
        .map(item => item.code);

    sortedCodes.forEach(code => {
        const pos = Math.max(0, Math.min(resultHtml.length, code.position));
        const tagType = code.tagType || 'PLACEHOLDER';
        const uniqueId = `${code.id}_${tagType}`;
        const token = code.data || '';
        const escaped = token.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const chip = `<span data-code data-code-id="${uniqueId}" data-original-data="${token}"
      style="background-color:#d1fae5; border:1px solid #6ee7b7; padding:0 4px; margin-right:2px;
             border-radius:4px; font-family:monospace; font-size:0.875rem; display:inline-block;
             vertical-align:baseline;">${escaped}</span>`;

        resultHtml = resultHtml.slice(0, pos) + chip + resultHtml.slice(pos);
    });

    return `<div>${resultHtml}</div>`;
};

const fromHtmlAndExtractCodes = (html, originalCodes) => {
    const el = document.createElement('div');
    el.innerHTML =
        html.startsWith('<div>') && html.endsWith('</div>')
            ? html.substring(5, html.length - 6)
            : html;

    const finalCodesMap = new Map();
    let cleanText = '';
    let currentPos = 0;

    const traverse = (node) => {
        if (node.nodeType === 3) {
            const textContent = node.textContent.replace(/[\u200b\u00a0\ufeff]/g, '');
            cleanText += textContent;
            currentPos += textContent.length;
        } else if (node.nodeType === 1) {
            if (node.hasAttribute('data-code')) {
                const uniqueId = node.getAttribute('data-code-id');
                const originalToken = node.getAttribute('data-original-data');
                if (uniqueId && originalToken) {
                    const originalCode = findCodeByUniqueId(uniqueId, originalCodes);
                    if (originalCode) {
                        finalCodesMap.set(uniqueId, {
                            ...originalCode,
                            position: currentPos,
                        });
                    }
                }
            } else if (node.tagName === 'BR') {
                cleanText += '\n';
                currentPos += 1;
            } else {
                if ((node.tagName === 'P' || node.tagName === 'DIV') && cleanText.length > 0 && !cleanText.endsWith('\n')) {
                    cleanText += '\n';
                    currentPos += 1;
                }
                Array.from(node.childNodes).forEach(traverse);
            }
        }
    };

    Array.from(el.childNodes).forEach(traverse);

    cleanText = cleanText.replace(/[ \t]*\n[ \t]*/g, '\n').trim();
    const finalCodesArray = Array.from(finalCodesMap.values()).sort(
        (a, b) => a.position - b.position
    );

    return { plainText: cleanText, finalCodesArray };
};

function getBucketName(gsUrl) {
    if (!gsUrl.startsWith("gs://")) return null;
    const path = gsUrl.slice(5);
    return path.split("/")[0];
}

/* ----------------------------------------------------
   EditableCell
---------------------------------------------------- */

const EditableCell = React.memo(({ segId, initialHtml, readOnly, onCommit, lang, isCodesVisible }) => {
    const ref = useRef(null);
    const lastHtml = useRef('');

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if ((initialHtml !== lastHtml.current && el.innerHTML !== initialHtml) || !el.innerHTML) {
            el.innerHTML = initialHtml;
            lastHtml.current = initialHtml;
        }
    }, [initialHtml]);

    const handleBlur = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const rawHtml = el.innerHTML;
        onCommit?.(segId, rawHtml);
        lastHtml.current = rawHtml;
    }, [onCommit, segId]);

    const containerClassName = `form-control p-2 text-base ${readOnly ? 'bg-light opacity-75 cursor-not-allowed' : ''} ${isCodesVisible ? '' : 'hide-codes'}`;

    return (
        <div
            ref={ref}
            contentEditable={!readOnly}
            suppressContentEditableWarning
            onBlur={handleBlur}
            dangerouslySetInnerHTML={{ __html: initialHtml }}
            lang={lang}
            spellCheck={true}
            className={containerClassName}
            style={{ minHeight: '40px', maxWidth: '100%', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
        />
    );
});
EditableCell.displayName = 'EditableCell';

const PAGE_SIZE = 50;

export default function OkapiDocumentSegmentsEditor({
    apiUrl,
    docGcsUrl,
    docTrlGcsUrl = '',
    docFileName,
    srcLang,
    trgLang,
    docId,
    userId,
    documentData,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const docsHook = useApiFetch(apiUrl);
    const segmentsHook = useApiFetch(apiUrl);

    const [selectedDocId, setSelectedDocId] = useState(null);
    const [currentDocName, setCurrentDocName] = useState('');
    const [docInfo, setDocInfo] = useState('');
    const [segments, setSegments] = useState([]);
    const [threshold, setThreshold] = useState(75);
    const [selectedTmId, setSelectedTmId] = useState('');
    const [showCodes, setShowCodes] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoadingDoc, setIsLoadingDoc] = useState(Boolean(docGcsUrl && docTrlGcsUrl));
    const [isImporting, setIsImporting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [mtSendPlainText, setMtSendPlainText] = useState(true);
    const [useAlignmentModel, setUseAlignmentModel] = useState(true);
    const [useCodesReinsertionModel, setUseCodesReinsertionModel] = useState(false);
    const [mtProvideConfidenceScores, setMtProvideConfidenceScores] = useState(false);
    const [dropdownPositions, setDropdownPositions] = useState({});

    const [mtDropdownOpen, setMtDropdownOpen] = useState(null);
    const mtDropdownRef = useRef(null);


    const segmentTargetValues = useRef({});
    const dirtySegments = useRef(new Set());

    // pagination
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const isFetchingRef = useRef(false);

    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);

    // Inject hide-codes css once
    useEffect(() => {
        if (!document.getElementById('hide-codes-style')) {
            const style = document.createElement('style');
            style.id = 'hide-codes-style';
            style.textContent = `
                .hide-codes span[data-code] {
                    border: none !important;
                    background: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    width: 0px !important;
                    height: 0px !important;
                    line-height: 0 !important;
                    font-size: 0 !important;
                    display: inline-block !important;
                    overflow: hidden !important;
                    color: transparent !important;
                    visibility: hidden !important;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        if (docGcsUrl && docTrlGcsUrl) {
            fetchSegments()
        }
    }, []);

    // Update local segments when segmentsHook.data changes
    useEffect(() => {
        // Skip the initial empty array so the loader isn't dismissed before the fetch resolves
        if (!segmentsHook.data?.length || !selectedDocId) return;

        const segs = segmentsHook.data.map((s) => ({
            id: s.id,
            segId: s.doc_seg_id,
            docId: s.doc_id,
            source: s.source?.textWithCodes ?? '',
            sourceText: s.source?.text ?? '',
            sourceCodes: s.source?.codes ?? [],
            target: s.target?.textWithCodes ?? '',
            targetText: s.target?.text ?? '',
            targetCodes: s.target?.codes ?? [],
            origin: s.origin,
            tmMatchScore: s.tm_match_score || 0,
            mtConfidenceScore: s.mt_confidence_score || 0,
            mtTargetIndex: s.mt_target_index ?? -1,
            mtTargets: s.mt_targets || [],
            position: s.position,
        }));

        if (segs.length && segs[0].docId !== selectedDocId) return;

        segs.forEach((s) => {
            if (!dirtySegments.current.has(s.segId)) {
                const layeredHtml = toHtmlWithIds(s.targetText, s.targetCodes);
                segmentTargetValues.current[s.segId] = layeredHtml;
            }
        });

        setSegments((prev) => {
            const prevById = new Map(prev.map((s) => [s.segId, s]));
            const updatedPageSegs = segs.map((remote) =>
                dirtySegments.current.has(remote.segId)
                    ? prevById.get(remote.segId) || remote
                    : remote
            );

            if (page === 1) {
                return updatedPageSegs;
            }

            const mergedMap = new Map(prev.map((s) => [s.segId, s]));
            updatedPageSegs.forEach((s) => mergedMap.set(s.segId, s));

            return Array.from(mergedMap.values()).sort((a, b) => a.position - b.position);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segmentsHook.data, selectedDocId]);

    // Recompute doc info
    useEffect(() => {
        if (!docsHook.data || !selectedDocId || segments.length === 0) return;

        const docMeta = docsHook.data.find((d) => d.doc_id === selectedDocId);
        const src = docMeta?.src_lang || srcLang;
        const trg = docMeta?.trg_lang || trgLang;
        setCurrentDocName(docMeta?.doc_file_name || '');

        const tmCount = segments.filter((s) => s.origin === 'TM').length;
        const mtCount = segments.filter((s) => s.origin === 'MT').length;
        const htCount = segments.filter((s) => s.origin === 'HT').length;
        const alCount = segments.filter((s) => s.origin === 'AL').length;

        setDocInfo(
            `${src} → ${trg} | ${segments.length} segments (TM: ${tmCount}, MT: ${mtCount},  AL: ${alCount}, HT: ${htCount})`
        );
    }, [segments, docsHook.data, selectedDocId, srcLang, trgLang]);

    const handleDirectImport = async () => {
        if (!docGcsUrl || !docGcsUrl.trim()) {
            setErrorMessage('GCS URL is required for direct import');
            return;
        }
        if (!docFileName || !docFileName.trim()) {
            setErrorMessage('Document file name is required');
            return;
        }

        const fd = new FormData();
        fd.append('doc_id', docId);
        fd.append('doc_file_name', docFileName);
        fd.append('doc_gcs_url', docGcsUrl);
        fd.append('doc_encoding', 'UTF-8');
        fd.append('src_lang', srcLang || 'en');
        fd.append('trg_lang', trgLang || 'fr');
        fd.append('user_id', userId);

        if (docTrlGcsUrl && docTrlGcsUrl.trim()) {
            fd.append('doc_trl_gcs_url', docTrlGcsUrl);
        }

        if (selectedTmId) {
            fd.append('tm_id', selectedTmId);
            fd.append('tm_threshold', threshold);
        }

        fd.append('mt_engine_id', 'GoogleMT');
        fd.append('mt_send_plain_text', mtSendPlainText);
        fd.append('use_alignment_model', useAlignmentModel);
        fd.append('use_codes_reinsertion_model', useCodesReinsertionModel);

        try {
            setIsImporting(true);
            setIsLoadingDoc(true);
            setSegments([]);
            setPage(1);
            setHasNext(true);
            isFetchingRef.current = false;
            setErrorMessage(null);

            const res = await fetch(`${apiUrl}/document`, {
                method: 'POST',
                body: fd
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Import request failed');
            }

            setSelectedDocId(docId);
            setDocInfo(`${srcLang || 'en'} → ${trgLang || 'fr'} | Importing...`);

            const poll = async () => {
                try {
                    const statusRes = await fetch(`${apiUrl}/document/${docId}/status`);
                    const data = await statusRes.json();

                    if (data.status === 'IMPORT_COMPLETED') {
                        const jsonSeg = await segmentsHook.fetchData(
                            `/document/${docId}/segment`,
                            1,
                            PAGE_SIZE
                        );
                        setPage(1);
                        setHasNext(jsonSeg.pagination?.has_next ?? false);
                        setIsImporting(false);
                        setIsLoadingDoc(false);
                    } else if (data.status === 'FAILED') {
                        setIsImporting(false);
                        setIsLoadingDoc(false);
                        setErrorMessage(data.error_message || 'Import failed with unknown error');
                    } else if (statusRes.status === 202) {
                        setTimeout(poll, 2000);
                    }
                } catch (err) {
                    setIsImporting(false);
                    setIsLoadingDoc(false);
                    setErrorMessage('Failed to check import status: ' + err.message);
                }
            };
            poll();
        } catch (err) {
            console.error(err);
            setIsImporting(false);
            setIsLoadingDoc(false);
            setErrorMessage('Failed to start import: ' + err.message);
        }
    };

    // New Workflow: Register Document (POST /document) -> Align Document (POST /document/{id}/align)
    const handleDualGcsImport = async () => {

        const newDocId = docId;

        // Request 3: Prioritize docFileName from the UI field, otherwise parse from GCS URL
        let fileName = docFileName.trim();
        if (!fileName) {
            // Fallback: Try to parse from GCS URL
            try {
                const parts = docGcsUrl.trim().split('/');
                if (parts.length > 0 && parts[parts.length - 1]) {
                    fileName = decodeURIComponent(parts[parts.length - 1]);
                }
            } catch (e) {
                console.warn('Could not extract filename from GCS URL', e);
            }
            if (!fileName || !fileName.trim()) {
                fileName = 'document_source'; // Final fallback
            }
        }

        setIsImporting(true);
        setErrorMessage(null);

        try {
            // Step 1: Create Document (Register GCS URL)
            const fd = new FormData();
            fd.append('doc_id', newDocId);
            fd.append('doc_gcs_url', docGcsUrl.trim());
            fd.append('doc_file_name', fileName); // Use the prioritized filename
            fd.append('user_id', userId);
            // doc_encoding default is UTF-8 per spec

            const createRes = await fetch(`${apiUrl}/document`, {
                method: 'POST',
                body: fd,
            });

            if (!createRes.ok) {
                const errData = await createRes.json().catch(() => null);
                throw new Error((errData && (errData.message || errData.error)) || `Failed to create document: ${createRes.status}`);
            }

            // Step 2: Trigger Alignment
            const alignBody = {
                src_lang: srcLang || 'en',
                trg_lang: trgLang || 'fr',
                doc_trl_gcs_url: docTrlGcsUrl.trim(),
                user_id: userId,
                use_alignment_model: !!useAlignmentModel,
                alignment_model_name: useAlignmentModel ? 'gemini-2.0-flash' : undefined,
                use_codes_reinsertion_model: !!useCodesReinsertionModel,
                codes_reinsertion_model_name: useCodesReinsertionModel ? 'gemini-2.5-flash' : undefined,
            };

            const alignRes = await fetch(`${apiUrl}/document/${encodeURIComponent(newDocId)}/align`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(alignBody),
            });

            if (!alignRes.ok) {
                const errData = await alignRes.json().catch(() => null);
                throw new Error((errData && (errData.message || errData.error)) || `Align request failed with status ${alignRes.status}`);
            }

            // Refresh list and select the new document
            // await docsHook.fetchData('/document', 1, 10000);
            setSelectedDocId(newDocId);
            setDocInfo(`${srcLang || 'en'} → ${trgLang || 'fr'} | Aligning...`);

            // Poll for completion
            const poll = async () => {
                try {
                    const statusRes = await fetch(`${apiUrl}/document/${encodeURIComponent(newDocId)}/status`);
                    const data = await statusRes.json();

                    if (data.status === 'IMPORT_COMPLETED' || data.status === 'EXPORT_COMPLETED') {
                        await segmentsHook.fetchData(`/document/${newDocId}/segment`, 1, 10000);
                        await docsHook.fetchData('/document', 1, 10000);
                        setIsImporting(false);
                        // Request 2: Do NOT clear GCS inputs on success.
                    } else if (data.status === 'FAILED') {
                        setIsImporting(false);
                        setErrorMessage(data.error_message || 'Alignment failed');
                    } else if (statusRes.status === 202) {
                        setTimeout(poll, 2000);
                    } else {
                        setTimeout(poll, 2000);
                    }
                } catch (err) {
                    setIsImporting(false);
                    console.error('Polling error:', err);
                    setErrorMessage('Failed to check alignment status: ' + err.message);
                }
            };
            poll();

        } catch (err) {
            console.error('Dual GCS Import error:', err);
            setIsImporting(false);
            setErrorMessage('Failed to register and align document: ' + err.message);
        }
    };

    const handleSingleGcsImport = async () => {

        const newDocId = docId;

        let fileName = docFileName.trim();
        if (!fileName) {
            try {
                const parts = docGcsUrl.trim().split('/');
                if (parts.length > 0 && parts[parts.length - 1]) {
                    fileName = decodeURIComponent(parts[parts.length - 1]);
                }
            } catch (e) {
                console.warn('Could not extract filename from GCS URL', e);
            }
            if (!fileName) {
                fileName = 'document';
            }
        }

        setIsImporting(true);
        setErrorMessage(null);

        try {
            // Step 1: Create document
            const fd = new FormData();
            fd.append('doc_id', newDocId);
            fd.append('doc_gcs_url', docGcsUrl.trim());
            fd.append('doc_file_name', fileName);
            fd.append('doc_encoding', 'UTF-8');
            fd.append('user_id', userId);

            const createRes = await fetch(`${apiUrl}/document`, {
                method: 'POST',
                body: fd,
            });

            if (!createRes.ok) {
                const errData = await createRes.json().catch(() => null);
                throw new Error((errData && (errData.message || errData.error)) || `Failed to create document: ${createRes.status}`);
            }

            // Step 2: Import document
            const importBody = {
                src_lang: srcLang || 'en',
                trg_lang: trgLang || 'fr',
                user_id: userId,
                tm_id: selectedTmId || undefined,
                tm_threshold: threshold || undefined,
                mt_engine_id: 'GoogleMTv3',
                mt_provide_confidence_scores: !!mtProvideConfidenceScores,
                mt_send_plain_text: !!mtSendPlainText,
                use_codes_reinsertion_model: !!useCodesReinsertionModel,
                codes_reinsertion_model_name: useCodesReinsertionModel ? 'gemini-2.5-flash' : undefined,
            };

            const importRes = await fetch(`${apiUrl}/document/${encodeURIComponent(newDocId)}/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(importBody),
            });

            if (!importRes.ok) {
                const errData = await importRes.json().catch(() => null);
                throw new Error((errData && (errData.message || errData.error)) || `Import failed: ${importRes.status}`);
            }

            await docsHook.fetchData('/document');
            setSelectedDocId(newDocId);
            setDocInfo(`${srcLang || 'en'} → ${trgLang || 'fr'} | Importing...`);

            // Step 3: Poll for completion
            const poll = async () => {
                try {
                    const statusRes = await fetch(`${apiUrl}/document/${encodeURIComponent(newDocId)}/status`);
                    const data = await statusRes.json();

                    if (data.status === 'IMPORT_COMPLETED') {
                        // await segmentsHook.fetchData(`/document/${newDocId}/segment`);
                        // await docsHook.fetchData('/document');
                        // setIsImporting(false);
                        const jsonSeg = await segmentsHook.fetchData(
                            `/document/${docId}/segment`,
                            1,
                            PAGE_SIZE
                        );
                        setPage(1);
                        setHasNext(jsonSeg.pagination?.has_next ?? false);
                        setIsImporting(false);
                        setIsLoadingDoc(false);
                    } else if (data.status === 'FAILED') {
                        setIsImporting(false);
                        setErrorMessage(data.error_message || 'Import failed');
                    } else if (statusRes.status === 202) {
                        setTimeout(poll, 2000);
                    }
                } catch (err) {
                    setIsImporting(false);
                    setErrorMessage('Failed to check import status: ' + err.message);
                }
            };
            poll();

        } catch (err) {
            console.error('Single GCS Import error:', err);
            setIsImporting(false);
            setErrorMessage('Failed to import from GCS: ' + err.message);
        }
    };

    const fetchSegments = async () => {
        setSelectedDocId(docId); // 🔥 REQUIRED
        setIsLoadingDoc(true);
        console.log("triggered from fetch segs");

        try {
            const jsonSeg = await segmentsHook.fetchData(
                `/document/${docId}/segment`,
                1,
                PAGE_SIZE
            );

            setPage(1);
            setHasNext(jsonSeg.pagination?.has_next ?? false);
        } catch (err) {
            setErrorMessage('Failed to load segments: ' + err.message);
        } finally {
            setIsImporting(false);
            setIsLoadingDoc(false);
        }
    };

    const handleExportClick = async () => {
        if (!selectedDocId) return;

        if (document.activeElement && document.activeElement.getAttribute('contenteditable') === 'true') {
            document.activeElement.blur();
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setIsExporting(true);
        setErrorMessage(null);
        await new Promise((r) => setTimeout(r, 0));

        try {
            const timestamp = Date.now();
            const gcsOutUrl = `gs://${getBucketName(docGcsUrl)}/post-edit/${timestamp}_${encodeURIComponent(docFileName)}`;
            console.log(gcsOutUrl, "gcsOutUrl");

            const res = await fetch(`${apiUrl}/document/${selectedDocId}/export`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    doc_out_gcs_url: gcsOutUrl,
                    tm_id: selectedTmId || undefined,
                }),
            });

            if (!res.ok) {
                const errorData = await res.json();

                if (res.status === 409) {
                    setIsExporting(false);
                    setErrorMessage(errorData.message || 'Export conflict - check document status');
                    return;
                }

                throw new Error(errorData.message || 'Export request failed');
            }

            const poll = async () => {
                try {
                    const statusRes = await fetch(`${apiUrl}/document/${selectedDocId}/status`);
                    const data = await statusRes.json();

                    if (data.status === 'EXPORT_COMPLETED') {
                        dispatch(
                            updatePostEditJobAction(
                                { job_id: docId, outputFilePath: gcsOutUrl },
                                (data) => {
                                    successToast(data.data.message);
                                    setIsExporting(false);
                                    navigate(`/post-edit-okapi/summary/${docId}`);
                                },
                                (data) => {
                                    errorToast(data.data.message);
                                    setIsExporting(false);
                                }
                            )
                        );
                    } else if (data.status === 'FAILED') {
                        setIsExporting(false);
                        setErrorMessage(data.error_message || 'Export failed with unknown error');
                    } else if (statusRes.status === 202) {
                        setTimeout(poll, 2000);
                    }
                } catch (err) {
                    console.error('Polling failed:', err);
                    setIsExporting(false);
                    setErrorMessage('Failed to check export status: ' + err.message);
                }
            };
            poll();
        } catch (err) {
            console.error('Export error:', err);
            setIsExporting(false);
            setErrorMessage('Failed to start export: ' + err.message);
        }
    };

    const handleCommit = useCallback(
        async (segId, rawEditorHtml) => {
            const seg = segments.find((s) => s.segId === segId);
            if (!seg) return;

            const { plainText, finalCodesArray } =
                fromHtmlAndExtractCodes(rawEditorHtml, seg.targetCodes);

            const normalizedCodes = finalCodesArray.map(c => ({
                ...c,
                position: Math.min(c.position, plainText.length),
            }));

            const prevHtml = segmentTargetValues.current[segId] || '';
            const { plainText: prevText, finalCodesArray: prevCodesArray } =
                fromHtmlAndExtractCodes(prevHtml, seg.targetCodes);

            const prevCodesJson = JSON.stringify(prevCodesArray);
            const newCodesJson = JSON.stringify(normalizedCodes);

            if (plainText === prevText && prevCodesJson === newCodesJson) {
                return;
            }

            dirtySegments.current.add(segId);
            segmentTargetValues.current[segId] = rawEditorHtml;

            const rebuiltTextWithCodes = buildTextWithCodes(plainText, normalizedCodes);

            const layeredTarget = {
                language: trgLang,
                text: plainText,
                codes: normalizedCodes,
                textWithCodes: rebuiltTextWithCodes,
            };

            setSegments((prev) =>
                prev.map((s) =>
                    s.segId === segId
                        ? {
                            ...s,
                            target: layeredTarget.textWithCodes,
                            targetText: layeredTarget.text,
                            targetCodes: layeredTarget.codes,
                            origin: 'HT',
                        }
                        : s
                )
            );

            try {
                const userId = crypto.randomUUID();

                const res = await fetch(`${apiUrl}/document/${selectedDocId}/segment/${segId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        target: layeredTarget,
                        origin: 'HT',
                        user_id: userId,
                    }),
                });

                if (!res.ok) {
                    const msg = await res.text();
                    throw new Error(msg || 'Server returned error');
                }
            } catch (err) {
                console.error('Save error:', err);
                setErrorMessage('Failed to save segment changes: ' + err.message);
            }
        },
        [apiUrl, selectedDocId, trgLang, segments]
    );

    // const getClassForOrigin = (o) => {
    //     switch (o) {
    //         case 'TM':
    //             return 'bg-success text-light';
    //         case 'MT':
    //             return 'bg-primary text-white';
    //         case 'HT':
    //             return 'bg-light text-dark border border-secondary';
    //         default:
    //             return 'bg-primary text-white';
    //     }
    // };

    // const getScoreDisplay = (seg) =>
    //     seg.origin === 'HT' ? (
    //         <FaUserCheck className="align-baseline text-secondary" style={{ width: '1.25rem', height: '1.25rem' }} />
    //     ) : seg.origin === 'MT' ? (
    //         'MT'
    //     ) : seg.origin === 'TM' ? (
    //         `${seg.tmMatchScore}%`
    //     ) : seg.origin === 'AL' ? (
    //         'AL'
    //     ) : (
    //         'N/A'
    //     );

    const isLoading = isImporting || isLoadingDoc || isExporting;

    // Load next page
    const loadNextPage = useCallback(async () => {
        if (isFetchingRef.current) return;
        if (!hasNext) return;
        if (!selectedDocId) return;

        isFetchingRef.current = true;
        setLoadingMore(true);

        try {
            const nextPage = page + 1;
            const json = await segmentsHook.fetchData(
                `/document/${selectedDocId}/segment`,
                nextPage,
                PAGE_SIZE
            );
            setPage(nextPage);
            setHasNext(json.pagination?.has_next ?? false);
        } catch (err) {
            console.error('Error loading next page:', err);
        } finally {
            isFetchingRef.current = false;
            setLoadingMore(false);
        }
    }, [hasNext, page, selectedDocId, segmentsHook]);

    // IntersectionObserver on sentinel row
    useEffect(() => {
        const root = scrollContainerRef.current;
        const target = sentinelRef.current;

        if (!root || !target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry.isIntersecting &&
                    hasNext &&
                    !loadingMore &&
                    !isFetchingRef.current &&
                    !isImporting &&
                    !isExporting &&
                    !isLoadingDoc
                ) {
                    loadNextPage();
                }
            },
            {
                root,
                threshold: 1.0,
            }
        );

        observer.observe(target);

        return () => observer.disconnect();
    }, [hasNext, loadingMore, isImporting, isExporting, isLoadingDoc, loadNextPage]);

    const getClassForOrigin = (o) =>
        o === 'TM'
            ? 'bg-success text-light'
            : o === 'MT'
                ? 'bg-primary text-white'
                : o === 'AL'
                    ? 'bg-purple text-light' // custom class
                    : o === 'HT'
                        ? 'bg-light text-dark'
                        : 'bg-primary text-warning';

    const getScoreDisplay = (seg) =>
        seg.origin === 'HT' ? (
            <FaUserCheck className="text-secondary" size={18} />
        ) : seg.origin === 'MT' ? (
            <div className="d-flex align-items-center gap-1">
                <span>MT</span>
                {console.log("seg.mtTargets", seg)}

                {seg.mtTargets?.length > 0 && (
                    <button
                        ref={(el) => {
                            if (el && !el.dataset.ignoreOutside) {
                                el.dataset.ignoreOutside = 'true';
                            }
                        }}
                        onClick={(e) => {
                            console.log("clicked");

                            e.stopPropagation();
                            const newOpen =
                                mtDropdownOpen === seg.segId ? null : seg.segId;
                            setMtDropdownOpen(newOpen);

                            if (newOpen) {
                                const rect =
                                    e.currentTarget.getBoundingClientRect();
                                const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                const spaceAbove = rect.top;

                                setDropdownPositions((prev) => ({
                                    ...prev,
                                    [seg.segId]:
                                        spaceBelow < 300 && spaceAbove > spaceBelow
                                            ? 'above'
                                            : 'below'
                                }));
                            }
                        }}
                        className="btn btn-sm btn-primary p-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                            <path d="M6 9L1.5 4.5h9L6 9z" />
                        </svg>
                    </button>
                )}
            </div>
        ) : seg.origin === 'TM' ? (
            `${seg.tmMatchScore}%`
        ) : seg.origin === 'AL' ? (
            'AL'
        ) : (
            'N/A'
        );

    const getConfidenceColor = (score) => {
        if (!score || score === 0 || score === 1.0) return 'bg-primary';
        if (score >= 0.5 && score < 1.0) return 'bg-warning';
        return 'bg-danger';
    };


    const handleMtTargetSelect = async (seg, targetInfo, index) => {
        const targetText = targetInfo.mt_target?.text || '';
        const targetCodes = targetInfo.mt_target?.codes || [];
        const confidenceScore = targetInfo.mt_confidence_score || 0;

        const layeredHtml = toHtmlWithIds(targetText, targetCodes);
        segmentTargetValues.current[seg.segId] = layeredHtml;
        dirtySegments.current.add(seg.segId);

        const rebuiltTextWithCodes = buildTextWithCodes(targetText, targetCodes);

        const layeredTarget = {
            language: trgLang,
            text: targetText,
            codes: targetCodes,
            textWithCodes: rebuiltTextWithCodes,
        };

        setSegments((prev) =>
            prev.map((s) =>
                s.segId === seg.segId
                    ? {
                        ...s,
                        target: layeredTarget.textWithCodes,
                        targetText: layeredTarget.text,
                        targetCodes: layeredTarget.codes,
                        mtConfidenceScore: confidenceScore,
                        mtTargetIndex: index,
                    }
                    : s
            )
        );

        try {


            const res = await fetch(`${apiUrl}/document/${encodeURIComponent(selectedDocId)}/segment/${encodeURIComponent(seg.segId)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    target: layeredTarget,
                    origin: 'MT',
                    user_id: userId,
                }),
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Server returned error');
            }
        } catch (err) {
            console.error('Save error:', err);
            setErrorMessage('Failed to save MT target selection: ' + err.message);
        }

        setMtDropdownOpen(null);
    };

    return (
        <div className="d-flex flex-column vh-100 bg-light overflow-auto">
            <div className='d-flex justify-content-between w-100 align-items-center p-2 position-sticky top-0 z-3 bg-light'>
                <div className='d-flex gap-4 w-100 align-items-center px-2'>
                    <CustomButton
                        disabled={true}
                        variant='primary'
                        className='d-flex align-items-center cursor_pointer fs_12'
                        style={{ height: '30px' }}
                    >
                        {availableLanguages.find(lang => lang?.value === documentData?.source_lang)?.label ?? "Loading..."}
                    </CustomButton>
                </div>
                <div className='d-flex justify-content-between gap-4 w-100 align-items-center'>
                    <div className='px-4'>
                        <CustomButton
                            disabled={true}
                            variant='primary'
                            className='d-flex align-items-center cursor_pointer fs_12'
                            style={{ height: '30px' }}
                        >
                            {availableLanguages.find(lang => lang?.value === documentData?.target_lang)?.label ?? "Loading..."}
                        </CustomButton>
                    </div>
                    <div className='d-flex gap-3 px-2'>
                        <CustomButton
                            variant='primary'
                            disabled={isLoading}
                            className='d-flex align-items-center fs_12'
                            style={{ height: '30px' }}
                            onClick={handleExportClick}
                        >
                            Save
                        </CustomButton>
                    </div>
                </div>
            </div>

            {errorMessage && (
                <Alert
                    variant="danger"
                    onClose={() => setErrorMessage(null)}
                    dismissible
                    className="m-3 flex-shrink-0"
                >
                    <p className="mb-0 text-sm font-weight-medium">
                        {errorMessage}
                    </p>
                </Alert>
            )}

            <Container fluid className="flex-grow-1 p- d-flex flex-column min-vh-0">
                <div className={`border rounded flex-grow-1 overflow-hidden position-relative ${isLoadingDoc ? 'opacity-50' : ''}`}>
                    <div
                        className="h-100 overflow-auto"
                        ref={scrollContainerRef}
                    >
                        <Table hover className="mb-0 table-fixed">
                            <thead className="bg-light sticky-top" style={{ zIndex: 1 }}>
                                <tr>
                                    <th className="text-start text-sm font-weight-semibold text-muted border-end" style={{ width: '60px' }}>#</th>
                                    <th className="text-start text-sm font-weight-semibold text-muted border-end" style={{ width: '50%' }}>Source</th>
                                    <th className="text-center text-sm" style={{ width: '80px' }}></th>
                                    <th className="text-start text-sm font-weight-semibold text-muted" style={{ width: '50%' }}>Target</th>
                                </tr>
                            </thead>
                            <tbody>
                                {segments.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted">
                                            {isLoading ? 'Loading segments...' : 'No segments found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {segments.map((seg) => {
                                            const escapedSourceText = (seg.sourceText || '')
                                                .replace(/&/g, '&amp;')
                                                .replace(/</g, '&lt;')
                                                .replace(/>/g, '&gt;');

                                            const sourceHtml = toHtmlWithIds(seg.sourceText, seg.sourceCodes);
                                            const sourceDisplayContent = showCodes
                                                ? sourceHtml
                                                : `<div>${escapedSourceText}</div>`;

                                            const targetHtml =
                                                segmentTargetValues.current[seg.segId] ||
                                                toHtmlWithIds(seg.targetText, seg.targetCodes);

                                            const isAbove = dropdownPositions[seg.segId] === 'above';

                                            return (
                                                <tr key={seg.segId} className="align-middle">
                                                    <td className="text-sm text-muted border-end">{seg.position}</td>
                                                    <td
                                                        className="text-base text-dark whitespace-pre-wrap word-break text-start border-end"
                                                        style={{ maxWidth: '0' }}
                                                    >
                                                        <div dangerouslySetInnerHTML={{ __html: sourceDisplayContent }} />
                                                    </td>
                                                    {/* <td className="text-start">
                                                        <span
                                                            className={`badge rounded-pill d-inline-flex align-items-center justify-content-center text-xs ${getClassForOrigin(seg.origin)}`}
                                                            style={{ padding: '0.4em 0.6em' }}
                                                        >
                                                            {getScoreDisplay(seg)}
                                                        </span>
                                                    </td> */}
                                                    <td className="px-2 py-2 border-bottom text-center">
                                                        <div className="position-relative d-inline-block">

                                                            <span
                                                                className={`px-1 rounded small fw-bold d-inline-flex align-items-center justify-content-center ${getClassForOrigin(seg.origin)}`}
                                                            >
                                                                {getScoreDisplay(seg)}
                                                            </span>

                                                            {mtDropdownOpen === seg.segId &&
                                                                seg.mtTargets &&
                                                                seg.mtTargets.length > 0 && (
                                                                    <div
                                                                        ref={mtDropdownRef}
                                                                        className="position-absolute bg-white border rounded shadow-lg d-flex flex-column"
                                                                        style={{
                                                                            zIndex: 9999,
                                                                            maxHeight: '500px',
                                                                            width: 'min(90vw, 900px)',
                                                                            left: '50%',
                                                                            transform: 'translateX(-50%)',
                                                                            ...(isAbove
                                                                                ? { bottom: '100%', marginBottom: '8px' }
                                                                                : { top: '100%', marginTop: '8px' })
                                                                        }}
                                                                    >
                                                                        {/* Header */}
                                                                        <div className="px-3 py-2 bg-light border-bottom small fw-medium text-secondary">
                                                                            {seg.sourceText}
                                                                        </div>

                                                                        {/* MT Targets */}
                                                                        <div className="overflow-auto">
                                                                            {seg.mtTargets.map((mtTarget, idx) => {
                                                                                const score = mtTarget.mt_confidence_score || 0;
                                                                                const text = mtTarget.mt_target?.text || '';
                                                                                const modelId = mtTarget.mt_model_id || 'Unknown';

                                                                                return (
                                                                                    <div
                                                                                        key={idx}
                                                                                        onClick={() =>
                                                                                            handleMtTargetSelect(seg, mtTarget, idx)
                                                                                        }
                                                                                        className="d-flex align-items-start gap-3 px-3 py-2 border-bottom text-start"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                    >
                                                                                        <div className="flex-shrink-0">
                                                                                            <div
                                                                                                className={`text-white px-2 py-1 rounded small fw-bold text-center ${getConfidenceColor(score)}`}
                                                                                                style={{ minWidth: '50px' }}
                                                                                            >
                                                                                                {score.toFixed(2)}
                                                                                            </div>
                                                                                        </div>

                                                                                        <div className="flex-grow-1 small text-dark text-break">
                                                                                            {text}
                                                                                        </div>

                                                                                        <div className="flex-shrink-0">
                                                                                            <span className="bg-secondary-subtle text-dark px-2 py-1 rounded small fw-medium">
                                                                                                {modelId}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </td>

                                                    <td className="position-relative text-start">
                                                        <EditableCell
                                                            key={seg.segId}
                                                            segId={seg.segId}
                                                            initialHtml={targetHtml}
                                                            readOnly={isLoadingDoc}
                                                            onCommit={handleCommit}
                                                            lang={trgLang}
                                                            isCodesVisible={showCodes}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* Sentinel row for infinite scroll */}
                                        <tr ref={sentinelRef}>
                                            <td colSpan="4" className="text-center py-2">
                                                {loadingMore && (
                                                    <Spinner size="sm" animation="border" />
                                                )}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </Container>

            <Modal
                show={isImporting || isLoadingDoc || isExporting}
                centered
                backdrop="static"
                keyboard={false}
            >
                <div className="text-center p-4">
                    <Spinner
                        animation="border"
                        variant="primary"
                        style={{ width: '3rem', height: '3rem' }}
                    />
                    <p className="mt-3 text-secondary font-weight-medium">
                        {isImporting
                            ? 'Loading document...'
                            : isExporting
                                ? 'Saving document...'
                                : 'Loading document...'}
                    </p>
                </div>
            </Modal>
        </div>
    );
}
